import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, HostListener, Input, OnDestroy, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormGroup,
  UntypedFormControl,
  Validators,
  FormArray,
  AbstractControl,
} from '@angular/forms';
import { ToastService } from '@core/components/toasts/toasts.service';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormInputType } from 'services/models/form-builder/enum/form-input-type.enum';
import { FormItemChartType } from 'services/models/form-builder/enum/form-item-chart-type.enum';
import { FormItemOptionModel } from 'services/models/form-builder/form-item-option.model';
import { FormItemModel } from 'services/models/form-builder/form-item.model';
import { guidEmpty } from 'utils/helps';
import {
  FormBuilderElementService,
  FormBuilderElementType,
} from '../services/form-builder-element.service';
import { OPTION_PICKER_ICONS } from './feather-icons.const';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-form-builder-question-manage-drawer',
  templateUrl: './form-builder-question-manage-drawer.component.html',
  styleUrls: ['./form-builder-question-manage-drawer.component.scss'],
})
export class FormBuilderQuestionManageDrawerComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  private previousInputType: string | null = null;

  private static readonly ICON_PICKER_HEIGHT = 340;
  private static readonly ICON_PICKER_WIDTH = 236;
  private static readonly ICON_PICKER_GAP = 6;
  private static readonly ICON_PICKER_MARGIN = 8;
  private static readonly NPS_VALUES = Array.from({ length: 11 }, (_, index) => `${index}`);

  private static readonly NUMERIC_SCALE_MIN = 2;
  private static readonly NUMERIC_SCALE_MAX = 10;
  
  @Input() item?: FormItemModel | null;
  @Input() parents?: FormItemModel[] = [];
  @Input() hasAnotherNpsItem = false;
  @Input() lastOrder? = 0;
  @Input() showChartType = false;

  public form!: FormGroup;
  public questionsTypes = FormInputType.buildListEnum();
  private readonly allChartTypes = FormItemChartType.buildListEnum();
  public filteredChartTypes: ReturnType<typeof FormItemChartType.buildListEnum> = [];
  public readonly optionPickerIcons = OPTION_PICKER_ICONS;
  public openOptionPickerIndex: number | null = null;
  public openOptionPickerPlacement: 'top' | 'bottom' = 'bottom';
  public pendingOptionIcon: string | null = null;
  public optionPickerUseViewportPosition = false;
  public optionPickerTop = 0;
  public optionPickerLeft = 0;
  public optionPickerWidth = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_WIDTH;
  public hasNpsConflict = false;
  public readonly npsValues = FormBuilderQuestionManageDrawerComponent.NPS_VALUES.map(value => Number(value));

  public numericScaleMaxControl = new UntypedFormControl(
    FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MAX,
    [
      Validators.min(FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MIN),
      Validators.max(FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MAX),
    ]
  );

  constructor(
    private _formBuilder: FormBuilder,
    private _modalActiveService: NgbActiveModal,
    private _toastService: ToastService,
    private _element: FormBuilderElementService
  ) {}

  ngOnInit(): void {
    this._createForm();

    this.previousInputType = this.inputTypeField.value;

    this._initNumericScaleFromItem();
    this.numericScaleMaxControl.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(value => {
        if (!this.isNumericScale) return;
        this._applyNumericScale(value);
      });

    if (this.inputTypeField.value === FormInputType.TRUE_OR_FALSE.value) {
      this._ensureTrueOrFalseOptions();
    }

    if (this.inputTypeField.value === FormInputType.NPS.value) {
      this._ensureNpsOptions();
    }

    this._updateNpsConflict(this.inputTypeField.value);

    if (this.showChartType && this.inputTypeField.value) {
      this._syncChartTypeWithInputType(this.inputTypeField.value);
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit(): void {
    if (this.showChartType) {
      const inputType = this.inputTypeField?.value;
      const allowedCharts = this.getAllowedChartTypesByInputType(inputType);

      if (allowedCharts.length === 0) {
        this.chartTypeField?.setValue(FormItemChartType.UNSET.value);
      } else {
        const chartType = this.chartTypeField?.value;
        if (!chartType || !allowedCharts.includes(chartType)) {
          this.chartTypeField?.setValue(null);
          this.form.markAllAsTouched();
          this.chartTypeField?.updateValueAndValidity();
          return;
        }
      }
    }

    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }

    if (this.hasNpsConflict) {
      return;
    }

    if (this.isGridField) {
      if (this.hasDuplicateGridRows || this.hasDuplicateGridColumns) {
        return;
      }

      this._replaceOptions(this._buildGridOptions());
    }

    if (this.isNpsField) {
      this._ensureNpsOptions();
    }

    if (this.isNumericScale) {
      this._applyNumericScale(this.numericScaleMaxControl.value);
    }

    if (this.showOptions) {
      const options = this.optionsArray?.value || [];
      const hasValidOption = options.some((opt: any) => !!opt.label?.trim());

      if (!hasValidOption) {
        this.optionsArray?.setErrors({ required: true });
        return;
      }

      // 🔹 Detecta quais opções foram alteradas
      const updatedOptions = options.map((opt: any) => {
        const original = this.item?.options?.find((o: any) => o.id === opt.id);
        const labelChanged = original && original.label !== opt.label;
        const iconChanged = original && original.iconName !== opt.iconName;
        return {
          ...opt,
          isUpdate: !!(labelChanged || iconChanged),
        };
      });

      // 🔹 Atualiza o formArray com os novos valores
      this.optionsArray.patchValue(updatedOptions);
    }

    const payload = this.getChangedItemId();
    if (!payload) {
      this._modalActiveService.close({ item: this.form.value });
      return;
    }

    this._element.update(payload).subscribe(confirmed => {
      if (confirmed) {
        this._modalActiveService.close({ item: this.form.value });
      }
    });
  }

  public handleClose() {
    this._modalActiveService.close();
  }

  private _createForm() {
    const gridRows = this._extractGridDimension('row');
    const gridColumns = this._extractGridDimension('column');
    const initialGridRows = gridRows.length
      ? gridRows
      : [{ label: 'Linha 1', order: 1, originalLabel: '' }];

    const shouldAllowNa =
      this.item?.inputType === FormInputType.TRUE_OR_FALSE.value &&
      (this.item?.options || []).some(
        o => (o?.label || '').trim().toLowerCase() === 'n/a' || o?.order === 3
      );

    this.form = this._formBuilder.group({
      id: [this.item?.id || ''],
      label: [this.item?.label || '', [Validators.required]],
      chartType: [this._resolveInitialChartType(), []],
      inputType: [this.item?.inputType || null, [Validators.required]],
      placeholder: [this.item?.placeholder || ''],
      description: [this.item?.description || ''],
      groupId: [this.item?.groupId || ''],
      parentItemId: [this.item?.parentItemId || null],
      parentItem: [this.item?.parentItem || null],
      required: [this.item?.required || false],
      order: [this.lastOrder || 0, [Validators.min(0)]],
      itemChildren: [this.item?.itemChildren || []],
      isChild: [this.item?.isChild || false],
      allowNA: [!!shouldAllowNa],
      gridColumnInput: [''],
      options: this._formBuilder.array(
        (this.item?.options || []).map(opt => this._createOptionGroup(opt))
      ),
      gridRows: this._formBuilder.array(
        initialGridRows.map(row => this._createGridDimensionGroup(row))
      ),
      gridColumns: this._formBuilder.array(
        gridColumns.map(column => this._createGridDimensionGroup(column))
      ),
    });
  }


  public get hasInsufficientOptions(): boolean {
    const type = this.inputTypeField.value;

    if (this.isGridField) {
      return this.gridRowsArray.length === 0 || this.gridColumnsArray.length === 0;
    }

    const mustHaveOptions = [
      FormInputType.SELECT.value,
      FormInputType.RADIO_OPTION.value,
      FormInputType.RADIO_OPTION_MULTISELECT.value,
    ];

    if (!mustHaveOptions.includes(type)) return false;
    if (type === FormInputType.TRUE_OR_FALSE.value) return false;
    return this.optionsArray.length < 2;
  }

  public onAllowNaChange(allow: boolean): void {
    if (this.inputTypeField.value !== FormInputType.TRUE_OR_FALSE.value) return;

    const naIndex = this.optionsArray.controls.findIndex(ctrl => {
      const value = ctrl.value;
      const label = (value?.label || '').trim().toLowerCase();
      return label === 'n/a' || value?.order === 3;
    });

    if (allow) {
      if (naIndex !== -1) return;
      this.optionsArray.push(
        this._createOptionGroup({
          label: 'N/A',
          order: 3,
        } as FormItemOptionModel)
      );
      return;
    }

    if (naIndex === -1) return;

    const control = this.optionsArray.controls[naIndex];
    const optionId = control?.value?.id;

    if (!this.item || this.item.id === guidEmpty || optionId === guidEmpty) {
      this.optionsArray.removeAt(naIndex);
      return;
    }

    this.removeOption(control);
  }

  private _ensureTrueOrFalseOptions(): void {
    if (this.optionsArray.length >= 2) return;

    const allowNa = !!this.form.get('allowNA')?.value;
    this.optionsArray.clear();

    this.optionsArray.push(
      this._createOptionGroup({
        label: 'Verdadeiro',
        order: 1,
      } as FormItemOptionModel)
    );
    this.optionsArray.push(
      this._createOptionGroup({
        label: 'Falso',
        order: 2,
      } as FormItemOptionModel)
    );

    if (allowNa) {
      this.optionsArray.push(
        this._createOptionGroup({
          label: 'N/A',
          order: 3,
        } as FormItemOptionModel)
      );
    }
  }

  private _ensureNpsOptions(): void {
    const currentOptions = [
      ...((this.optionsArray.value || []) as FormItemOptionModel[]),
      ...((this.item?.options || []) as FormItemOptionModel[]),
    ];

    this.optionsArray.clear();

    FormBuilderQuestionManageDrawerComponent.NPS_VALUES.forEach((label, index) => {
      const existingOption = currentOptions.find(
        option => (option.label || '').trim() === label
      );

      this.optionsArray.push(
        this._createOptionGroup(FormItemOptionModel.create({
          id: existingOption?.id || guidEmpty,
          formItemId: existingOption?.formItemId || guidEmpty,
          label,
          legend: '',
          order: index + 1,
          isUpdate: false,
          iconName: null,
        }))
      );
    });
  }

  public getTrueOrFalseOptionIcon(index: number): string {
    if (index === 0) return '✓';
    if (index === 1) return '✗';
    return '';
  }

  private _createOptionGroup(option?: FormItemOptionModel): FormGroup {
    return this._formBuilder.group({
      id: [option?.id || guidEmpty],
      formItemId: [option?.id || guidEmpty],
      label: [option?.label || '', Validators.required],
      legend: [option?.legend || ''],
      order: [option?.order || 0, Validators.required],
      isUpdate: [false],
      iconName: [option?.iconName || null],
    });
  }

  public toggleOptionPicker(index: number, event?: Event): void {
    event?.stopPropagation();

    if (this.openOptionPickerIndex === index) {
      this.closeOptionPicker();
      return;
    }

    const currentIcon = this.optionsArray.at(index)?.get('iconName')?.value ?? null;
    this.pendingOptionIcon = currentIcon;

    const trigger = event?.currentTarget as HTMLElement | null;
    const triggerRect = trigger?.getBoundingClientRect();

    if (triggerRect) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const isSmallScreen = viewportWidth <= 600;
      const margin = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_MARGIN;
      const gap = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_GAP;
      const panelHeight = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_HEIGHT;
      const maxPanelWidth = viewportWidth - margin * 2;
      const panelWidth = Math.max(180, Math.min(FormBuilderQuestionManageDrawerComponent.ICON_PICKER_WIDTH, maxPanelWidth));

      const availableBelow = window.innerHeight - triggerRect.bottom;
      const availableAbove = triggerRect.top;
      const shouldOpenTop =
        availableBelow < panelHeight &&
        availableAbove > availableBelow;

      this.openOptionPickerPlacement = shouldOpenTop ? 'top' : 'bottom';

      if (!isSmallScreen) {
        this.optionPickerUseViewportPosition = false;
        this.optionPickerTop = 0;
        this.optionPickerLeft = 0;
        this.optionPickerWidth = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_WIDTH;
        this.openOptionPickerIndex = index;
        return;
      }

      this.optionPickerUseViewportPosition = true;

      let left = triggerRect.right - panelWidth;
      if (left < margin) {
        left = margin;
      }
      const maxLeft = viewportWidth - panelWidth - margin;
      if (left > maxLeft) {
        left = maxLeft;
      }

      let top = shouldOpenTop ? triggerRect.top - panelHeight - gap : triggerRect.bottom + gap;
      const maxTop = viewportHeight - panelHeight - margin;
      if (top < margin) {
        top = margin;
      }
      if (top > maxTop) {
        top = Math.max(margin, maxTop);
      }

      this.optionPickerWidth = panelWidth;
      this.optionPickerTop = top;
      this.optionPickerLeft = left;
    }

    this.openOptionPickerIndex = index;
  }

  public closeOptionPicker(): void {
    this.pendingOptionIcon = null;
    this.openOptionPickerIndex = null;
    this.openOptionPickerPlacement = 'bottom';
    this.optionPickerUseViewportPosition = false;
    this.optionPickerTop = 0;
    this.optionPickerLeft = 0;
    this.optionPickerWidth = FormBuilderQuestionManageDrawerComponent.ICON_PICKER_WIDTH;
  }

  public setPendingOptionIcon(icon: string): void {
    this.pendingOptionIcon = this.pendingOptionIcon === icon ? null : icon;
  }

  public confirmOptionIcon(optionGroup: AbstractControl): void {
    optionGroup.get('iconName')?.setValue(this.pendingOptionIcon);
    this.closeOptionPicker();
  }

  public getPendingOptionIcon(optionGroup: AbstractControl): string | null {
    return this.pendingOptionIcon ?? optionGroup.get('iconName')?.value ?? null;
  }

  public removeOptionIcon(optionGroup: AbstractControl): void {
    optionGroup.get('iconName')?.setValue(null);
    this.closeOptionPicker();
  }

  public get canRemoveOptionIcon(): boolean {
    if (this.openOptionPickerIndex === null) return false;
    const currentIcon = this.optionsArray.at(this.openOptionPickerIndex)?.get('iconName')?.value;
    return !!currentIcon;
  }

  public get canConfirmOptionIcon(): boolean {
    return !!this.pendingOptionIcon;
  }

  private _createGridDimensionGroup(dimension?: {
    label: string;
    order: number;
    originalLabel: string;
  }): FormGroup {
    return this._formBuilder.group({
      label: [dimension?.label || '', Validators.required],
      order: [dimension?.order || 0, Validators.required],
      originalLabel: [dimension?.originalLabel || ''],
    });
  }

  private _extractGridDimension(type: 'row' | 'column') {
    const values = (this.item?.options || [])
      .slice()
      .sort((left, right) => (left.order || 0) - (right.order || 0));
    const seen = new Set<string>();
    const dimensions: { label: string; order: number; originalLabel: string }[] = [];

    values.forEach(option => {
      const label = (type === 'row' ? option.label : option.legend || '').trim();
      if (!label) {
        return;
      }

      const normalized = label.toLowerCase();
      if (seen.has(normalized)) {
        return;
      }

      seen.add(normalized);
      dimensions.push({
        label,
        order: dimensions.length + 1,
        originalLabel: label,
      });
    });

    return dimensions;
  }

  private _buildGridOptions(): FormItemOptionModel[] {
    const existingOptions = this.item?.options || [];
    const rows = this.gridRowsArray.value || [];
    const columns = this.gridColumnsArray.value || [];
    const options: FormItemOptionModel[] = [];

    rows.forEach((row: any, rowIndex: number) => {
      columns.forEach((column: any, columnIndex: number) => {
        const existingOption = existingOptions.find(option => {
          const matchesRow = (option.label || '').trim() === (row.originalLabel || '').trim();
          const matchesColumn = (option.legend || '').trim() === (column.originalLabel || '').trim();
          return matchesRow && matchesColumn;
        });

        const rowLabel = (row.label || '').trim();
        const columnLabel = (column.label || '').trim();
        const labelChanged = !!existingOption && (existingOption.label || '').trim() !== rowLabel;
        const legendChanged = !!existingOption && (existingOption.legend || '').trim() !== columnLabel;

        options.push({
          id: existingOption?.id || guidEmpty,
          formItemId: existingOption?.formItemId || guidEmpty,
          label: rowLabel,
          legend: columnLabel,
          order: rowIndex * columns.length + columnIndex + 1,
          isUpdate: labelChanged || legendChanged,
        } as FormItemOptionModel);
      });
    });

    return options;
  }

  private _replaceOptions(options: FormItemOptionModel[]): void {
    this.optionsArray.clear();
    options.forEach(option => this.optionsArray.push(this._createOptionGroup(option)));
  }

  private _refreshGridDimensionOrder(array: FormArray): void {
    array.controls.forEach((control, index) => {
      control.get('order')?.setValue(index + 1);
    });
  }

  private _buildGridDimensionSnapshot(array: FormArray) {
    return (array.value || []).map((dimension: any, index: number) => ({
      label: (dimension?.label || '').trim(),
      order: index + 1,
      originalLabel: dimension?.originalLabel || '',
    }));
  }

  private _hasDuplicateDimensions(values: { label: string }[]): boolean {
    const normalized = values
      .map(value => (value.label || '').trim().toLowerCase())
      .filter(value => !!value);

    return new Set(normalized).size !== normalized.length;
  }

  public addOption(): void {
    this.optionsArray.push(
      this._createOptionGroup({
        order: this.optionsArray.length + 1,
      } as FormItemOptionModel)
    );
  }

  public addGridRow(defaultLabel = ''): void {
    this.gridRowsArray.push(
      this._createGridDimensionGroup({
        label: defaultLabel,
        order: this.gridRowsArray.length + 1,
        originalLabel: '',
      })
    );
  }

  public addGridColumn(label?: string): void {
    const normalizedLabel = (label ?? this.gridColumnInputField.value ?? '').trim();
    if (!normalizedLabel) {
      return;
    }

    const currentDimensions = this._buildGridDimensionSnapshot(this.gridColumnsArray);
    const initialDimensions = this._extractGridDimension('column');
    const existingDimension = [...currentDimensions, ...initialDimensions].find(
      dimension => dimension.label.toLowerCase() === normalizedLabel.toLowerCase()
    );

    if (
      this.gridColumnsArray.controls.some(
        control =>
          ((control.get('label')?.value as string) || '').trim().toLowerCase() ===
          normalizedLabel.toLowerCase()
      )
    ) {
      this.gridColumnInputField.setValue('');
      return;
    }

    this.gridColumnsArray.push(
      this._createGridDimensionGroup({
        label: normalizedLabel,
        order: this.gridColumnsArray.length + 1,
        originalLabel: existingDimension?.originalLabel || '',
      })
    );

    this.gridColumnInputField.setValue('');
  }

  public removeGridRow(index: number): void {
    if (this.gridRowsArray.length <= 1) {
      this._toastService.showWarning(
        'É necessário manter pelo menos uma linha na grade.',
        'top-center'
      );
      return;
    }

    this.gridRowsArray.removeAt(index);
    this._refreshGridDimensionOrder(this.gridRowsArray);
  }

  public removeGridColumn(index: number): void {
    this.gridColumnsArray.removeAt(index);
    this._refreshGridDimensionOrder(this.gridColumnsArray);
  }

  public onGridColumnInputKeydown(event: KeyboardEvent): void {
    if (!['Enter', ','].includes(event.key)) {
      return;
    }

    event.preventDefault();
    this.addGridColumn();
  }

  public onGridColumnInputBlur(): void {
    this.addGridColumn();
  }

  public onGridDimensionDrop(
    event: CdkDragDrop<AbstractControl[]>,
    array: FormArray
  ): void {
    if (!array || event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(array.controls, event.previousIndex, event.currentIndex);
    this._refreshGridDimensionOrder(array);
    array.updateValueAndValidity();
  }

  onParentItemChange(item: FormItemModel): void {
    if (item) {
      this.form.get('parentItemId')?.setValue(item.id);
      this.form.get('parentItem')?.setValue(item);
      this.form.get('order')?.setValue(item.order + 1);
      this.form.get('isChild')?.setValue(true);
    } else {
      this.form.get('parentItemId')?.setValue('');
      this.form.get('parentItem')?.setValue(null);
      this.form.get('order')?.setValue(this.lastOrder || 0);
      this.form.get('isChild')?.setValue(false);
    }
  }

  public removeOption(control: AbstractControl): void {
    const option = control.value;

    const index = this.optionsArray.controls.indexOf(control);
    if (index === -1) {
      this._toastService.showError('Opção não encontrado.', 'top-center');
      return;
    }

    const payload = {
      groupId: guidEmpty,
      itemId: guidEmpty,
      optionId: option.id,
    } as FormBuilderElementType;

    this._element.remove(
      payload,
      () => this.optionsArray.removeAt(index),
      'Opção removida com sucesso.',
      'Erro ao remover opção.'
    );
  }

  public onInputTypeChange(type: FormInputType | string) {
    if (!type) return;

    const selectedTypeValue = typeof type === 'string' ? type : type.value;
    const previousType = this.previousInputType ?? this.item?.inputType ?? this.inputTypeField.value;

    this._updateNpsConflict(selectedTypeValue, true);

    const optionTypes = [
      FormInputType.SELECT.value,
      FormInputType.RADIO_OPTION.value,
      FormInputType.RADIO_OPTION_MULTISELECT.value,
      FormInputType.MULTISELECT.value,
      FormInputType.NUMERIC_SCALE.value,
      FormInputType.NPS.value,
      FormInputType.RADIO_OPTION_GRID.value,
      FormInputType.RADIO_OPTION_MULTISELECT_GRID.value,
    ];
    const gridTypes = [
      FormInputType.RADIO_OPTION_GRID.value,
      FormInputType.RADIO_OPTION_MULTISELECT_GRID.value,
    ];

    if (previousType === FormInputType.NUMERIC_SCALE.value && selectedTypeValue !== FormInputType.NUMERIC_SCALE.value) {
      this.optionsArray.clear();
    }

    if (
      previousType === FormInputType.TRUE_OR_FALSE.value &&
      selectedTypeValue !== FormInputType.TRUE_OR_FALSE.value
    ) {
      this.optionsArray.clear();
      this.form.get('allowNA')?.setValue(false);
    }

    if (
      previousType === FormInputType.NPS.value &&
      selectedTypeValue !== FormInputType.NPS.value
    ) {
      this.optionsArray.clear();
    }

    if (
      !optionTypes.includes(selectedTypeValue) &&
      this.optionsArray.length > 0
    ) {
      this._toastService.showWarning(
        'É necessário limpar as opções antes de alterar o tipo do campo.',
        'top-center'
      );
      this.form.get('inputType')?.setValue(previousType);
      return;
    }

    if (
      previousType &&
      this.optionsArray.length > 0 &&
      gridTypes.includes(previousType) !== gridTypes.includes(selectedTypeValue)
    ) {
      this._toastService.showWarning(
        'Não é possível alternar entre grade e seleção comum em um item já configurado.',
        'top-center'
      );
      this.form.get('inputType')?.setValue(previousType);
      return;
    }

    if (gridTypes.includes(selectedTypeValue) && this.gridRowsArray.length === 0) {
      this.addGridRow('Linha 1');
    }

    if (selectedTypeValue === FormInputType.TRUE_OR_FALSE.value) {
      this.optionsArray.clear();

      const allowNa = !!this.form.get('allowNA')?.value;
      this.optionsArray.push(
        this._createOptionGroup({
          label: 'Verdadeiro',
          order: 1,
        } as FormItemOptionModel)
      );
      this.optionsArray.push(
        this._createOptionGroup({
          label: 'Falso',
          order: 2,
        } as FormItemOptionModel)
      );

      if (allowNa) {
        this.optionsArray.push(
          this._createOptionGroup({
            label: 'N/A',
            order: 3,
          } as FormItemOptionModel)
        );
      }
    }

    if (selectedTypeValue === FormInputType.NPS.value) {
      this.form.get('allowNA')?.setValue(false);
      this._ensureNpsOptions();
    }

    if (selectedTypeValue === FormInputType.NUMERIC_SCALE.value) {
      this.form.get('allowNA')?.setValue(false);
      this._applyNumericScale(this.numericScaleMaxControl.value);
    }

    if (this.showChartType) {
      this._syncChartTypeWithInputType(selectedTypeValue);
    }

    this.previousInputType = selectedTypeValue;
  }

  private _syncChartTypeWithInputType(inputType: string): void {
    this.recalculateFilteredChartTypes(inputType);

    const allowedCharts = this.getAllowedChartTypesByInputType(inputType);

    if (allowedCharts.length === 0) {
      this.chartTypeField?.setValue(FormItemChartType.UNSET.value);
      this._setChartTypeRequired(false);
      return;
    }

    this._setChartTypeRequired(true);

    const currentChartType = this.chartTypeField?.value;
    if (
      currentChartType &&
      currentChartType !== FormItemChartType.UNSET.value &&
      !allowedCharts.includes(currentChartType)
    ) {
      this.chartTypeField?.setValue(null);
    }

    if (
      (!this.chartTypeField?.value ||
        this.chartTypeField.value === FormItemChartType.UNSET.value) &&
      allowedCharts.length === 1
    ) {
      this.chartTypeField?.setValue(allowedCharts[0]);
    }
  }

  private _setChartTypeRequired(required: boolean): void {
    if (!this.chartTypeField) return;

    this.chartTypeField.setValidators(required ? [Validators.required] : []);
    this.chartTypeField.updateValueAndValidity({ emitEvent: false });
  }

  public get hasChartTypeOptions(): boolean {
    if (!this.showChartType || !this.inputTypeField?.value) {
      return false;
    }

    return this.getAllowedChartTypesByInputType(this.inputTypeField.value).length > 0;
  }

  public getChangedItemId(): FormBuilderElementType | undefined {
    if (!this.item || this.item.id === guidEmpty) return;

    const oldItemLabel = (this.item.label || '').trim();
    const newItemLabel = (this.form.get('label')?.value || '').trim();

    if (oldItemLabel !== newItemLabel) {
      return {
        itemId: this.item.id,
        groupId: guidEmpty,
        optionId: guidEmpty,
      } as FormBuilderElementType;
    }

    const originalOptions = this.item.options ?? [];
    const currentOptions = this.optionsArray.value ?? [];

    const hasChangedOption = currentOptions.some((opt: any) => {
      if (opt.id === guidEmpty) return false;

      const originalOpt = originalOptions.find(o => o.id === opt.id);
      if (!originalOpt) return false;

      const oldLabel = (originalOpt.label || '').trim();
      const newLabel = (opt.label || '').trim();

      return oldLabel !== newLabel || originalOpt.iconName !== opt.iconName;
    });

    if (hasChangedOption) {
      return {
        itemId: this.item.id,
        groupId: guidEmpty,
        optionId: guidEmpty,
      } as FormBuilderElementType;
    }

    return;
  }

  public get showOptions() {
    return [
      FormInputType.SELECT.value,
      FormInputType.RADIO_OPTION.value,
      FormInputType.RADIO_OPTION_MULTISELECT.value,
      FormInputType.MULTISELECT.value,
    ].includes(this.inputTypeField.value);
  }

  public get isNumericScale(): boolean {
    return this.inputTypeField.value === FormInputType.NUMERIC_SCALE.value;
  }

  public get numericScalePreviewOptions(): number[] {
    const max = this._clampNumericScaleMax(Number(this.numericScaleMaxControl.value));
    return Array.from({ length: max }, (_, index) => index + 1);
  }

  public get numericScaleGridTemplate(): string {
    const length = this.numericScalePreviewOptions.length;
    return `repeat(${length}, 1fr)`;
  }

  public get numericScaleThumbLeft(): number {
    const min = FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MIN;
    const max = FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MAX;
    const value = this._clampNumericScaleMax(Number(this.numericScaleMaxControl.value));
    const percent = ((value - min) / (max - min)) * 100;
    return Math.max(0, Math.min(100, percent));
  }

  public get isNpsField() {
    return this.inputTypeField.value === FormInputType.NPS.value;
  }

  public get isRadioOption() {
    return this.inputTypeField.value === FormInputType.RADIO_OPTION.value;
  }

  public get isSaveDisabled(): boolean {
    return (
      this.form.invalid ||
      this.hasInsufficientOptions ||
      this.hasDuplicateGridRows ||
      this.hasDuplicateGridColumns ||
      this.hasNpsConflict
    );
  }

  public get showGridConfigurator() {
    return this.isGridField;
  }

  public get isSingleChoiceGrid(): boolean {
    return this.inputTypeField.value === FormInputType.RADIO_OPTION_GRID.value;
  }

  public get showPlaceholder() {
    return [FormInputType.TEXT.value, FormInputType.TEXTAREA.value].includes(
      this.inputTypeField.value
    );
  }

  public get isFieldTrueOrFalse() {
    return this.inputTypeField.value === FormInputType.TRUE_OR_FALSE.value;
  }

  public get isGridField() {
    return [
      FormInputType.RADIO_OPTION_GRID.value,
      FormInputType.RADIO_OPTION_MULTISELECT_GRID.value,
    ].includes(this.inputTypeField.value);
  }

  public get hasDuplicateGridRows(): boolean {
    return this._hasDuplicateDimensions(this.gridRowsArray.value || []);
  }

  public get hasDuplicateGridColumns(): boolean {
    return this._hasDuplicateDimensions(this.gridColumnsArray.value || []);
  }

  public get isChild() {
    return this.form?.get('isChild')?.value;
  }

  public get inputTypeField() {
    return this.form.get('inputType') as UntypedFormControl;
  }

  public get chartTypeField() {
    return this.form.get('chartType') as UntypedFormControl;
  }

  private _resolveInitialChartType(): string | null {
    if (!this.showChartType) {
      return this.item?.chartType || FormItemChartType.UNSET.value;
    }

    if (!this.item?.chartType || this.item.chartType.toUpperCase() === FormItemChartType.UNSET.value) {
      return null;
    }

    return this.item.chartType.toUpperCase();
  }

  private recalculateFilteredChartTypes(inputType: string | null | undefined): void {
    const allowed = this.getAllowedChartTypesByInputType(inputType);
    this.filteredChartTypes = this.allChartTypes.filter(ct => allowed.includes(ct.value));
  }

  private getAllowedChartTypesByInputType(inputType: string | null | undefined): string[] {
    if (!inputType) {
      return [];
    }

    switch (inputType) {
      case FormInputType.TEXT.value:
      case FormInputType.TEXTAREA.value:
        return [FormItemChartType.COMMENT.value];
      case FormInputType.DATE.value:
        return [FormItemChartType.TEMPORAL_EVOLUTION.value];
      case FormInputType.NUMERIC_SCALE.value:
      case FormInputType.NPS.value:
        return [FormItemChartType.THERMOMETER.value];
      case FormInputType.TRUE_OR_FALSE.value:
        return [FormItemChartType.BINARY_THERMOMETER.value];
      case FormInputType.MULTISELECT.value:
        return [FormItemChartType.PIZZA.value, FormItemChartType.BAR.value];
      case FormInputType.RADIO_OPTION.value:
      case FormInputType.SELECT.value:
        return [
          FormItemChartType.THERMOMETER.value,
          FormItemChartType.BINARY_THERMOMETER.value,
          FormItemChartType.PIZZA.value,
          FormItemChartType.BAR.value,
        ];
      default:
        return [];
    }
  }

  public get optionsArray(): FormArray {
    return this.form.get('options') as FormArray;
  }

  public get gridRowsArray(): FormArray {
    return this.form.get('gridRows') as FormArray;
  }

  public get gridColumnsArray(): FormArray {
    return this.form.get('gridColumns') as FormArray;
  }

  public get gridColumnInputField() {
    return this.form.get('gridColumnInput') as UntypedFormControl;
  }

  public get drawerTitle() {
    return this.item?.id ? 'Editar item do formulário' : 'Adicionar item ao formulário';
  }

  public get isRemoving(): boolean {
    return this._element.isRemoving;
  }

  private _initNumericScaleFromItem(): void {
    const max = this._inferNumericScaleMax();
    this.numericScaleMaxControl.setValue(max, { emitEvent: false });

    if (this.isNumericScale) {
      this._applyNumericScale(max);
    }
  }

  private _inferNumericScaleMax(): number {
    const optionsCount = (this.item?.options || []).length;
    const fallback = FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MAX;
    const raw = optionsCount || fallback;
    return this._clampNumericScaleMax(raw);
  }

  private _applyNumericScale(rawValue: any): void {
    const max = this._clampNumericScaleMax(Number(rawValue));

    const currentOptions = [
      ...((this.optionsArray.value || []) as FormItemOptionModel[]),
      ...((this.item?.options || []) as FormItemOptionModel[]),
    ];

    this.optionsArray.clear();
    for (let i = 1; i <= max; i++) {
      const label = String(i);
      const existingOption = currentOptions.find(
        option => (option.label || '').trim() === label
      );

      this.optionsArray.push(
        this._createOptionGroup({
          id: existingOption?.id || guidEmpty,
          formItemId: existingOption?.formItemId || guidEmpty,
          label,
          order: i,
        } as FormItemOptionModel)
      );
    }
    this.optionsArray.updateValueAndValidity({ emitEvent: false });
  }

  private _clampNumericScaleMax(value: number): number {
    const min = FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MIN;
    const max = FormBuilderQuestionManageDrawerComponent.NUMERIC_SCALE_MAX;

    if (!Number.isFinite(value)) return max;
    if (value < min) return min;
    if (value > max) return max;
    return Math.floor(value);
  }

  private _inputTypeUsesOptions(typeValue: string): boolean {
    return [
      FormInputType.SELECT.value,
      FormInputType.RADIO_OPTION.value,
      FormInputType.RADIO_OPTION_MULTISELECT.value,
      FormInputType.MULTISELECT.value,
      FormInputType.TRUE_OR_FALSE.value,
      FormInputType.NUMERIC_SCALE.value,
      FormInputType.NPS.value,
    ].includes(typeValue);
  }

  private _updateNpsConflict(selectedTypeValue: string | null, shouldAlert = false): void {
    const hadConflict = this.hasNpsConflict;
    const isCurrentItemNps = this.item?.inputType === FormInputType.NPS.value;

    this.hasNpsConflict =
      selectedTypeValue === FormInputType.NPS.value && this.hasAnotherNpsItem && !isCurrentItemNps;

    if (this.hasNpsConflict && shouldAlert && !hadConflict) {
      this._toastService.showWarning(
        'Já existe um campo NPS neste formulário. Escolha outro tipo de entrada para continuar.',
        'top-right'
      );
    }
  }

  @HostListener('document:click')
  public onDocumentClick(): void {
    this.closeOptionPicker();
  }
}
